import type { FC } from 'react'
import { useState } from 'react'

import { Input, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useMutation } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import RefreshIcon from 'mdi-react/RefreshIcon'

import authResources from '@/api/auth'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'

const AccessCodeForm: FC<{
  userName: string
}> = ({ userName }) => {
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()

  const [isGenerateCodeButtonDisabled, setIsGenerateCodeButtonDisabled] = useState(false)
  const [isErrorPopupMessageOpen, setIsErrorPopupMessageOpen] = useState(false)

  const generatePasswordMutation = useMutation(authResources.generatePassword)

  const handleGenerateCode = () => {
    generatePasswordMutation.mutate(userName, {
      onSuccess() {
        setIsGenerateCodeButtonDisabled(true)

        setTimeout(() => {
          setIsGenerateCodeButtonDisabled(false)
        }, 5 * 1000)
      },
      onError() {
        setIsErrorPopupMessageOpen(true)
      },
    })
  }

  const hasPasswordBeenGenerated =
    generatePasswordMutation.isError || generatePasswordMutation.isSuccess

  return (
    <>
      <div className="send-code-button-container">
        <Button
          className="with-full mx-0"
          size="medium"
          variant={hasPasswordBeenGenerated ? 'secondary' : 'primary'}
          Icon={hasPasswordBeenGenerated ? <RefreshIcon /> : undefined}
          disabled={isGenerateCodeButtonDisabled}
          isLoading={generatePasswordMutation.isLoading}
          onClick={handleGenerateCode}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          {hasPasswordBeenGenerated ? (
            <Translation id="Try again" />
          ) : (
            <Translation id="Send code by SMS" />
          )}
        </Button>
      </div>

      <Field name="accessCode">
        {({ field, meta }: FieldProps) => (
          <Input
            isFullWidth
            capitalizeLabel
            id="accessCode"
            ariaLabel={translate('Code')}
            marginBottom={16}
            error={meta.error}
            touched={meta.touched}
            placeholder={translate('Enter your code')}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            {...field}
          />
        )}
      </Field>

      <MessagePopup
        message={
          <Translation id="We couldn't generate code for this number. Please make sure the provided number is correct." />
        }
        title={<Translation id="Code generation error" />}
        isOpen={isErrorPopupMessageOpen}
        onClose={() => setIsErrorPopupMessageOpen(false)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    </>
  )
}

export default AccessCodeForm
